// Comprehensive Arkansas family law data based on FindLaw and official Arkansas sources
const arkansasData = {
  'arkansas': {
    name: 'Arkansas',
    overview: {
      keyPoints: [
        'Arkansas law (Act 604 of 2021) creates a rebuttable presumption that joint custody is in the best interest of the child',
        'The state uses the "Income Shares Model" for child support calculations, governed by Administrative Order No. 10',
        'Arkansas recognizes both "covenant" and non-covenant marriages, though covenant marriages are rare',
        'Divorce requires establishing grounds (fault-based) or proving 18 months of separation for no-fault',
        'Grandparents have statutory rights to petition for visitation if specific conditions are met',
        'Relocation cases are determined by what is in the best interest of the child, often shifting the burden in joint custody scenarios'
      ],
      uniqueFeatures: [
        'The "General Indignities" ground for divorce is widely used and unique in its broad application',
        'Arkansas has a distinct two-step residency requirement: 60 days to file, but 3 months of residency before a decree can be entered',
        'The presumption of joint custody is stronger than in many other states, requiring "clear and convincing evidence" to rebut',
        'Arkansas does not use a standardized statewide parenting plan form, allowing for more flexible but potentially ambiguous drafting'
      ]
    },
    legalRequirements: {
      residency: 'To file, one party must reside in Arkansas for 60 days. To finalize, residency must be proven for 3 full months.',
      filing: 'File a Complaint for Divorce or Petition for Paternity/Custody in the Circuit Court of the county where the complainant resides',
      paternity: 'Established via Voluntary Acknowledgment of Paternity (AOP) at birth, administrative order through OCSE, or court order',
      documentation: 'Complaint, Summons, Confidential Information Sheet, and verifying affidavit of residency by a third-party witness',
      uniqueRules: [
        'A third-party witness is required to testify (or sign an affidavit) verifying the residency of the filing party',
        'There is a mandatory 30-day waiting period after filing before a divorce decree can be granted',
        'Arkansas courts often require mediation in contested custody cases before a trial date is set'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Refers to the actual physical care and residence of the child; joint physical custody is the default presumption',
      legalCustody: 'Refers to the right to make major decisions (medical, educational, religious); usually shared in joint custody arrangements',
      jointCustody: 'Favored by law (A.C.A. § 9-13-101); implies equal time and shared responsibility unless there is proof of danger or unfitness',
      visitation: 'Standard schedules (like the "standard visitation schedule") are used when joint custody is not awarded; typically alternating weekends',
      specialProvisions: [
        'Rights of parents are subordinate to the welfare and best interest of the child',
        'Sex offender registration by a parent or household member creates a presumption against custody',
        'Arkansas law allows for "grandparent visitation" petitions under A.C.A. § 9-13-103 if the nuclear family is severed (e.g., divorce, death)'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'A specific schedule for physical custody (weekdays, weekends)',
        'Holiday and vacation division (typically alternating years)',
        'Transportation arrangements and exchange locations',
        'Decision-making authority for health, education, and religion',
        'Methods for resolving disputes (e.g., mediation requirement)',
        'Provisions for relocation or moving out of the school district'
      ],
      tips: [
        'Since Arkansas lacks a mandatory statewide form, be extremely detailed in drafting to avoid future ambiguity',
        'Clearly define "General Indignities" if used as grounds to ensure they don\'t negatively impact custody discussions',
        'Include a "Right of First Refusal" clause for childcare needs',
        'Address communication methods (e.g., OurFamilyWizard) specifically in the order'
      ]
    },
    childSupport: {
      guidelines: 'Governed by Arkansas Supreme Court Administrative Order No. 10, utilizing an Income Shares Model',
      factors: [
        'Combined gross income of both parents',
        'Number of children',
        'Work-related childcare expenses',
        'Health insurance premiums for the child',
        'Extraordinary medical expenses',
        'Parenting time adjustments (abatement may apply for extended visitation)'
      ],
      calculation: 'Base support is determined from the Family Support Chart based on combined income, then pro-rated between parents',
      modifications: 'Can be modified upon a showing of a material change in circumstances or every 3 years for a review',
      enforcement: 'Enforced by the Office of Child Support Enforcement (OCSE) via income withholding, tax intercept, and license suspension'
    },
    resources: {
      courts: [
        {
          name: 'Arkansas Judiciary - Court Information',
          url: 'https://www.arcourts.gov/',
          description: 'Official site for Arkansas courts, including docket search (CourtConnect) and forms'
        },
        {
          name: 'Arkansas Legal Services Partnership',
          url: 'https://www.arlegalservices.org/',
          description: 'Provides online forms, fact sheets, and legal assistance for low-income residents'
        },
        {
          name: 'Administrative Order No. 10 (Child Support)',
          url: 'https://www.arcourts.gov/rules-and-administrative-orders/administrative-orders/number-10',
          description: 'Official text of the child support guidelines and family support chart'
        }
      ],
      support: [
        {
          name: 'Arkansas Office of Child Support Enforcement (OCSE)',
          url: 'https://www.dfa.arkansas.gov/child-support/',
          description: 'State agency handling support collection, establishment, and enforcement'
        },
        {
          name: 'Center for Arkansas Legal Services',
          url: 'https://www.arkansaslegal.org/',
          description: 'Non-profit law firm providing free legal services to eligible Arkansans'
        },
        {
          name: 'Arkansas Coalition Against Domestic Violence',
          url: 'https://www.domesticpeace.com/',
          description: 'Resources and shelter information for victims of domestic violence'
        }
      ],
      legal: [
        {
          name: 'Arkansas Bar Association - Find a Lawyer',
          url: 'https://www.arkbar.com/for-the-public/find-a-lawyer',
          description: 'Directory of licensed attorneys in Arkansas'
        },
        {
          name: 'Access to Justice Commission',
          url: 'https://arkansasjustice.org/',
          description: 'Resources for self-represented litigants and access to legal help'
        }
      ],
      educational: [
        {
          name: 'Arkansas Law Help - Family Law',
          url: 'https://www.arlegalservices.org/family',
          description: 'Guides on divorce, custody, and support specific to Arkansas law'
        },
        {
          name: 'OurFamilyWizard - Arkansas',
          url: 'https://www.ourfamilywizard.com/',
          description: 'Co-parenting communication tool often ordered by Arkansas courts'
        }
      ]
    },
    bestInterestsFactors: [
      'The psychological relationship between the parent and the child',
      'The need for stability and continuity in the child\'s relationship with parents and siblings',
      'The past conduct of the parents toward the child',
      'The reasonable preference of the child (if of sufficient age and mental capacity)',
      'The moral fitness, physical health, and mental health of the parents',
      'The home, school, and community record of the child',
      'The willingness of each parent to facilitate and encourage a continuing parent-child relationship with the other parent',
      'Evidence of domestic abuse or sexual abuse',
      'Whether a parent is a registered sex offender',
      'The work schedules and availability of the parents'
    ]
  }
};

export default arkansasData;